import { useState, useEffect, useCallback, useMemo } from "react";
import { ModalWithTitle } from "@scripts/react/components/RfModal/ModalWithTitle";
import { ModalWithIcon } from "@scripts/react/components/RfModal/ModalWithIcon";
import { MarkdownPreview } from "@scripts/react/components/MarkdownPreview";
import { RunWorkflowModalContent } from "@scripts/react/components/Workflows/EditorComponents/RunWorkflowModal/RunWorkflowModalContent";
import {
    LANGUAGES,
    API_KEY_PLACEHOLDER,
    replaceApiKeyPlaceholder
} from "@scripts/react/components/Workflows/EditorComponents/DeployCodeGenerators";
import type {
    WorkflowData,
    ImageMode,
    VideoMode,
    LanguageOption,
    BatchModalConfig,
    QuickTipSection
} from "@scripts/react/components/Workflows/EditorComponents/RunWorkflowModal/types";
import { ModelArchitecture } from "@rapid/components/flow/deploy/Steps/Model/types";
import { useDeployContext } from "@rapid/contexts/DeployContext";
import { useCurrentWorkflow } from "@rapid/hooks/useCurrentWorkflow";
import { useFlow } from "@rapid/contexts/FlowContext";
import { useFlowState } from "@rapid/hooks/useFlowState";
import { useBatchJobNotifications } from "@rapid/hooks/useBatchJobNotifications";
import { BatchProcessingModal } from "@rapid/components/flow/shared/Deploy/BatchProcessingModal";
import { FeatureNotAvailableModal } from "@rapid/components/flow/shared/Deploy/FeatureNotAvailableModal";
import {
    useCurrentModelChange,
    useBatchJobs,
    useStartBatchJobsSubscription,
    useStopBatchJobsSubscription
} from "@rapid/store";
import { useCurrentWorkspace } from "@rapid/hooks/useCurrentWorkspace";
import { Flows, DeploySteps } from "@rapid/types/flows";
import { useAnalytics } from "@rapid/contexts/AnalyticsContext";
import { AnalyticsFlowEvent } from "@rapid/analytics/types";

interface RapidDeployModalProps {
    isOpen: boolean;
    onClose: () => void;
}

type SubModal = "none" | "gated" | "featureNotAvailable" | "terminalScript" | "batchProcessing";

export function RapidDeployModal({ isOpen, onClose }: RapidDeployModalProps) {
    const { workflowCredentials } = useDeployContext();
    const { currentWorkflow: workflow } = useCurrentWorkflow();
    const { task } = useFlow();
    const { setStep } = useFlowState();
    const { currentWorkspace: workspace } = useCurrentWorkspace();
    const startBatchJobsSubscription = useStartBatchJobsSubscription();
    const stopBatchJobsSubscription = useStopBatchJobsSubscription();
    const { track } = useAnalytics();

    // Model gating: SAM3 models can only use the hosted API
    const currentModelChange = useCurrentModelChange();
    const isModelGated =
        !currentModelChange || currentModelChange.to.architecture === ModelArchitecture.SAM3;

    // Sub-modal state: which modal is currently shown instead of the deploy modal
    const [subModal, setSubModal] = useState<SubModal>("none");

    // Reset sub-modal when the parent re-opens the deploy modal
    useEffect(() => {
        if (isOpen) {
            setSubModal("none");
            track({ event: AnalyticsFlowEvent.DEPLOY_MODAL_OPENED });
        }
    }, [isOpen, track]);

    // Modal state
    const [dataType, setDataType] = useState(0); // 0 = images, 1 = video
    const [currentImageMode, setCurrentImageMode] = useState<ImageMode>("hosted");
    const [currentVideoMode, setCurrentVideoMode] = useState<VideoMode>("api");
    const [selectedLanguage, setSelectedLanguage] = useState<LanguageOption>(LANGUAGES[0]);
    const [isBatchModalOpen, setIsBatchModalOpen] = useState(false); // notification-triggered only
    const [batchJobType, setBatchJobType] = useState<"images" | "videos">("images");
    const [batchShowJobs, setBatchShowJobs] = useState(true);
    const [terminalScriptJobType, setTerminalScriptJobType] = useState<"images" | "videos">(
        "images"
    );

    // Subscribe to batch jobs for notifications
    useEffect(() => {
        if (!workspace?.id) return;
        startBatchJobsSubscription(workspace.id);
        return () => stopBatchJobsSubscription();
    }, [workspace?.id, startBatchJobsSubscription, stopBatchJobsSubscription]);

    const handleViewBatchResults = useCallback(() => setIsBatchModalOpen(true), []);
    useBatchJobNotifications(handleViewBatchResults);

    // Build workflow data for the presentational component
    const { workspaceUrl, workflowUrl, apiKey } = workflowCredentials;
    const workflowData = useMemo<WorkflowData>(
        () => ({
            workflowUrl,
            workflowName: workflow?.name ?? workflowUrl,
            workspaceUrl,
            endpointUrl: `https://detect.roboflow.com/infer/workflows/${workspaceUrl}/${workflowUrl}`,
            localDeployUrl: "http://localhost:9001",
            apiKey,
            workflowParameters: [],
            workflowImages: [{ name: "image" }],
            isLocalEmbedded: false,
            isInstantModel: false,
            workflowConfigJSON: null
        }),
        [workflowUrl, workflow?.name, workspaceUrl, apiKey]
    );

    // Gating + local interception: intercept mode changes
    const handleImageModeChange = useCallback(
        (mode: ImageMode) => {
            track({ event: AnalyticsFlowEvent.DEPLOY_IMAGE_MODE_CLICKED, data: { mode } });
            if (mode === "local") {
                setSubModal("featureNotAvailable");
                return;
            }
            if (isModelGated && mode !== "hosted") {
                setSubModal("gated");
                return;
            }
            setCurrentImageMode(mode);
        },
        [isModelGated, track]
    );

    const handleVideoModeChange = useCallback(
        (mode: VideoMode) => {
            track({ event: AnalyticsFlowEvent.DEPLOY_VIDEO_MODE_CLICKED, data: { mode } });
            if (mode === "local") {
                setSubModal("featureNotAvailable");
                return;
            }
            if (isModelGated && mode !== "api") {
                setSubModal("gated");
                return;
            }
            setCurrentVideoMode(mode);
        },
        [isModelGated, track]
    );

    // Gated modal: dismiss → back to deploy
    const handleGatedDismiss = useCallback(() => {
        setSubModal("none");
    }, []);

    // Gated modal: "Upload More Images" → navigate to improve, close everything
    const handleGatedImproveModel = useCallback(() => {
        track({ event: AnalyticsFlowEvent.DEPLOY_UPLOAD_MORE_DATA_CLICKED });
        onClose();
        setStep({ flow: Flows.DEPLOY, step: DeploySteps.IMPROVE });
    }, [onClose, setStep, track]);

    // Feature not available: dismiss from survey → back to deploy
    const handleFeatureDismiss = useCallback(() => {
        setSubModal("none");
    }, []);

    // Feature not available: survey completed → close everything
    const handleFeatureComplete = useCallback(() => {
        onClose();
    }, [onClose]);

    // Feature not available: "Start Deploying" (RTSP) → back to deploy with Video → API
    const handleFeatureStartRTSP = useCallback(() => {
        setDataType(1);
        setCurrentVideoMode("api");
        setSubModal("none");
    }, []);

    // Batch modal integration (opened from deploy modal → subModal)
    const handleOpenBatchModal = useCallback(
        (_files: File[], config: BatchModalConfig) => {
            track({
                event: AnalyticsFlowEvent.DEPLOY_BATCH_MODAL_OPENED,
                data: { jobType: config.jobType }
            });
            setBatchJobType(config.jobType === "videos" ? "videos" : "images");
            setBatchShowJobs(false);
            setSubModal("batchProcessing");
        },
        [track]
    );

    // View existing jobs (opened from deploy modal → subModal)
    const batchJobs = useBatchJobs();
    const handleViewJobs = useCallback(
        (jobType: "images" | "videos") => {
            track({ event: AnalyticsFlowEvent.DEPLOY_VIEW_JOBS_CLICKED, data: { jobType } });
            setBatchJobType(jobType);
            setBatchShowJobs(true);
            setSubModal("batchProcessing");
        },
        [track]
    );

    // Terminal script modal (opened from deploy modal → subModal)
    const handleOpenTerminalScript = useCallback(
        (jobType: "images" | "videos") => {
            track({ event: AnalyticsFlowEvent.DEPLOY_TERMINAL_SCRIPT_OPENED, data: { jobType } });
            setTerminalScriptJobType(jobType);
            setSubModal("terminalScript");
        },
        [track]
    );

    const terminalScript = useMemo(() => {
        const batchType = terminalScriptJobType === "images" ? "images" : "videos";
        return `\`\`\`bash
# Install the CLI
pip install inference-cli

# Upload files and create a batch
inference rf-cloud data-staging create-batch-of-${batchType} \\
  --data-source local-file-system \\
  --batch-id my-batch \\
  --source-path /path/to/your/${batchType}

# Trigger the job
inference rf-cloud processing submit-job \\
  --workspace ${workspaceUrl} \\
  --workflow-id ${workflowUrl} \\
  --batch-id my-batch \\
  --api-key ${API_KEY_PLACEHOLDER}
\`\`\``;
    }, [workspaceUrl, workflowUrl, terminalScriptJobType]);

    const handleLanguageSelect = useCallback(
        (language: LanguageOption) => {
            track({
                event: AnalyticsFlowEvent.DEPLOY_LANGUAGE_SELECTED,
                data: { language: language.text }
            });
            setSelectedLanguage(language);
        },
        [track]
    );

    const handleClose = useCallback(() => {
        track({ event: AnalyticsFlowEvent.DEPLOY_MODAL_CLOSED });
        onClose();
    }, [onClose, track]);

    // Quick tips — no-op for Rapid (tips are hidden via isSidebarCollapsed=false)
    const handleQuickTipClick = useCallback(async (_section: QuickTipSection) => {}, []);

    const handleBatchModalClose = useCallback(() => {
        if (subModal === "batchProcessing") {
            setSubModal("none");
        } else {
            setIsBatchModalOpen(false);
        }
    }, [subModal]);

    const handleTerminalScriptClose = useCallback(() => setSubModal("none"), []);

    const hasExistingJobs = useMemo(() => batchJobs.length > 0, [batchJobs.length]);

    return (
        <>
            {/* Deploy modal — hidden when a sub-modal is active */}
            <ModalWithTitle
                isOpen={isOpen && subModal === "none"}
                onClose={handleClose}
                size="xl"
                title="Deploy Model"
                scrollInsideModal={true}
                className="withSidebar"
                panelClassName="h-screen"
                showFooter={false}
            >
                <RunWorkflowModalContent
                    workflowData={workflowData}
                    dataType={dataType}
                    currentImageMode={currentImageMode}
                    currentVideoMode={currentVideoMode}
                    onDataTypeChange={setDataType}
                    onImageModeChange={handleImageModeChange}
                    onVideoModeChange={handleVideoModeChange}
                    selectedLanguage={selectedLanguage}
                    onLanguageSelect={handleLanguageSelect}
                    isSidebarCollapsed={false}
                    onOpenBatchModal={handleOpenBatchModal}
                    onClose={handleClose}
                    isMobileWorkflowAvailable={false}
                    onQuickTipClick={handleQuickTipClick}
                    isWorkflowEditor={false}
                    onOpenTerminalScript={handleOpenTerminalScript}
                    hasExistingJobs={hasExistingJobs}
                    onViewJobs={handleViewJobs}
                />
            </ModalWithTitle>

            {/* Gated model — "More data needed" */}
            <ModalWithIcon
                isOpen={isOpen && subModal === "gated"}
                onClose={handleGatedDismiss}
                type="warning"
                icon="far fa-triangle-exclamation"
                title="More data needed"
                size="sm"
                showFooter={false}
            >
                <p className="text-center text-sm text-gray-600">
                    To unlock this feature, your model needs more data to train a version that
                    supports it.
                </p>
                <button
                    className="w-full rounded-lg bg-purple-600 px-4 py-3 text-sm font-medium text-white hover:bg-purple-700"
                    onClick={handleGatedImproveModel}
                >
                    Upload More Images
                </button>
            </ModalWithIcon>

            {/* Feature not available — local deploy survey */}
            <FeatureNotAvailableModal
                isOpen={isOpen && subModal === "featureNotAvailable"}
                onDismiss={handleFeatureDismiss}
                onComplete={handleFeatureComplete}
                onStartRTSP={handleFeatureStartRTSP}
            />

            {/* Batch processing modal — from deploy (subModal) or notification (independent) */}
            <BatchProcessingModal
                isOpen={isBatchModalOpen || (isOpen && subModal === "batchProcessing")}
                onClose={handleBatchModalClose}
                jobType={batchJobType}
                showJobs={batchShowJobs}
            />

            {/* Terminal script modal */}
            <ModalWithTitle
                isOpen={isOpen && subModal === "terminalScript"}
                onClose={handleTerminalScriptClose}
                title="Terminal Script"
                size="lg"
                showFooter={false}
            >
                <div
                    data-color-mode="light"
                    className="overflow-auto rounded-lg border border-gray-200 [&>div]:m-0 [&>div]:w-full [&_pre]:m-0 [&_pre]:rounded-lg"
                >
                    <MarkdownPreview
                        source={terminalScript}
                        sanitize={false}
                        onCopy={(text) => replaceApiKeyPlaceholder(text, apiKey)}
                    />
                </div>
            </ModalWithTitle>
        </>
    );
}
